import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { Async } from '../../platform/async/Async'
import { Skeleton } from '../../platform/async/Skeleton'
import { PremiumGate } from '../../platform/access/PremiumGate'
import { useEntitlement } from '../../platform/access/useEntitlement'
import { useToast } from '../../platform/feedback/ToastProvider'
import { emit } from '../../platform/analytics/analytics'
import { useScreenViewed } from '../../platform/analytics/useScreenViewed'
import { ButtonPreview } from '../../platform/primitives/ButtonPreview'
import { Button } from '../../platform/primitives/Button'
import { usePageMeta } from '../../shell/PageMeta'
import { CategoriesToolbar } from './CategoriesToolbar'
import { useCategoryView } from './useCategoryView'
import { CategoryDeleteDialog } from './CategoryDeleteDialog'
import { CategoryForm } from './CategoryForm'
import { CategoryTable } from './CategoryTable'
import type { Category } from './categoryTree'
import { useCategories, useMostUsed } from './useCategories'
import './categories.css'

/**
 * The categories list — segmented by type, each parent's children indented
 * beneath it.
 *
 * `empty.title` is supplied because `Async`'s props require it, though the
 * state is unreachable — 88 system categories are seeded for every user. The
 * explanation lives in `useCategories`, not repeated here.
 */
const NO_CATEGORIES: Category[] = []

/** Shared by the live button and its `ButtonPreview` in the locked gate (#578). */
const NEW_CATEGORY_VARIANT = 'primary'
const NEW_CATEGORY_SIZE = 'page'
const NEW_CATEGORY_LABEL = 'New category'

export function Categories() {
  const state = useCategories()
  // What "viewed" means: `useScreenViewed`'s docblock.
  useScreenViewed(state, (categories) =>
    emit('categories_viewed', {
      categories_count: categories.length,
      custom_categories_count: categories.filter((c) => !c.is_system).length,
    }),
  )
  // Read HERE and handed down, not read inside `CategoryTable`. The table
  // takes its rows as a prop and is presentational; the route is what owns
  // server state. It is a further observer of the one categories cache
  // entry, so this is no extra request. See `CategoryTable`'s prop docblock
  // for the second reason, which is about the browser sweeps.
  const mostUsed = useMostUsed()
  /* One shared empty, not a fresh `[]` per render — a new array identity each
     pass would rebuild the whole tree inside `useCategoryView` on every render
     while the list is loading. */
  const navigate = useNavigate()
  // The route owns the delete confirm and its toast (#376), as `Household`
  // does: `CategoryTable` stays presentational (it is rendered bare by
  // `Probe.tsx`), and `main.tsx` mounts the one `ToastProvider` above the
  // routed screen. The row itself, not its id, so the dialog can name it
  // after a refetch has changed the list under it.
  const toast = useToast()
  const [deleting, setDeleting] = useState<Category | undefined>(undefined)
  // `'create'` or the row being edited — one inline form at a time, so a
  // second boolean alongside it could only ever disagree with this one.
  const [formTarget, setFormTarget] = useState<'create' | Category | undefined>(undefined)

  // The same read `<PremiumGate>` below makes internally, so `locked_create_shown`
  // can fire from the ONE place this task may touch — `PremiumGate.tsx` is not on
  // its file list, and it has no seam of its own for "I just rendered locked".
  const entitled = useEntitlement('custom_categories')
  // Fires once per transition into locked, not once per render — same
  // ref-guard shape `MaintenanceGate.tsx` uses for `maintenance_shown`, since a
  // breakpoint change or a parent re-render would otherwise re-announce a gate
  // whose lock state never changed.
  const announcedLocked = useRef(false)
  useEffect(() => {
    if (!entitled && !announcedLocked.current) {
      announcedLocked.current = true
      emit('locked_create_shown', { entitlement: 'custom_categories' })
    } else if (entitled) {
      announcedLocked.current = false
    }
  }, [entitled])

  /* Title and count live in the chrome's top bar, not on the canvas — the
     breadcrumb already reads "Organise / Categories", so a second <h2> said
     the same word twice. `null` while loading, so the bar shows no line rather
     than the word "Loading…" beside the breadcrumb. Matches `Merchants`. */
  usePageMeta(state.status === 'success' ? `${state.data.length} categories` : null)

  /* M11-R18: gated on the control itself, so the gate fires when the user
     reaches for it — not after the form is filled. */
  const newCategory = (
    <PremiumGate
      entitlement="custom_categories"
      preview={
        <ButtonPreview variant={NEW_CATEGORY_VARIANT} size={NEW_CATEGORY_SIZE}>
          {NEW_CATEGORY_LABEL}
        </ButtonPreview>
      }
      onUpgrade={() => {
        /* M13 billing owns the destination; nothing here yet. */
      }}
    >
      <Button variant={NEW_CATEGORY_VARIANT} size={NEW_CATEGORY_SIZE} onClick={() => setFormTarget('create')}>
        {NEW_CATEGORY_LABEL}
      </Button>
    </PremiumGate>
  )

  /* The view is built from the rows we have — an empty list while loading or
     on an error — so the toolbar below renders in EVERY Async state rather
     than only in success. That is the whole point of hoisting this out of
     `CategoryTable`: the filter, the sort and "New category" used to vanish
     with the table. */
  const view = useCategoryView(state.status === 'success' ? state.data : NO_CATEGORIES, mostUsed)

  return (
    <>
      <CategoriesToolbar view={view} action={newCategory} />
      {deleting !== undefined && (
        <CategoryDeleteDialog
          category={deleting}
          categories={view.categories}
          onClose={() => setDeleting(undefined)}
          onDeleted={(movedTo) =>
            toast.show({
              kind: 'success',
              message:
                movedTo === undefined
                  ? `${deleting.name} deleted. Any transactions are now uncategorized.`
                  : `${deleting.name} deleted. Any transactions moved to ${movedTo}.`,
            })
          }
        />
      )}
      {/* No CTA row here: `New category` lives in `CategoryTable`'s toolbar
          row, beside "Expand all" — Sushil, 2026-08-21, matching `Merchants`.

          KNOWN COST, stated rather than hidden. `CategoryTable` renders only
          in Async's SUCCESS branch, so the control is absent while the list is
          loading and on an error. Two lesser options were tried and are worse:
          a second copy of the button outside `Async` moves it between two
          containers when the data lands, which detaches a click already in
          flight (a test caught exactly that); and its own row keeps it always
          available but is the two-row layout this change exists to remove.

          The right fix is to hoist the toolbar's `query`/`sort`/`open` state
          up here so the whole row renders unconditionally, the way
          `MerchantsToolbar` does — a real refactor, deliberately not smuggled
          into a layout change. Until then the loss is bounded: the create
          form's parent picker is fed from this same list, so on an error it
          would open with no parents to choose from anyway. */}
      {formTarget !== undefined && (
        /* No `<Panel>` wrapper any more — the form IS a modal, and `Overlay`
           paints its own panel. Wrapping it would paint and pad a second
           surface inside the dialog's own.

           `key` still varies with the target so switching from editing one row
           to another — without cancelling — remounts the form instead of
           re-rendering it. `name`/`type`/`color`/`icon` are seeded once, in
           `useState`'s initial value, so without this a switch keeps the FIRST
           row's in-progress edits while `editing.id` (read fresh every render)
           points at the second — a save that looks successful and silently
           overwrites the wrong row. */
        <CategoryForm
          key={formTarget === 'create' ? 'create' : formTarget.id}
          onClose={() => setFormTarget(undefined)}
          editing={formTarget === 'create' ? undefined : formTarget}
          parents={
            state.status === 'success' ? state.data.filter((c) => c.parent_id == null) : []
          }
        />
      )}
      {/* No `<Panel>` here any more — `CategoryTable` wraps the GRID in its
          own, leaving its toolbar on the canvas above it. Wrapping the whole
          `Async` would put the toolbar back inside the card, which is the
          arrangement being replaced. The skeleton and error states are
          unpanelled for the same reason `Merchants` leaves them so: they
          replace the grid, not the page. */}
      <Async
          surface="categories"
          state={state}
          skeleton={
            <>
              <Skeleton height={44} />
              <Skeleton height={44} />
              <Skeleton height={44} />
              <Skeleton height={44} />
              <Skeleton height={44} />
              <Skeleton height={44} />
              <Skeleton height={44} />
              <Skeleton height={44} />
            </>
          }
          empty={{ title: 'No categories' }}
          announce={(categories) => `${categories.length} categories`}
        >
          {() => (
            <CategoryTable
              view={view}
              onEdit={setFormTarget}
              onDelete={setDeleting}
              onActivate={(c) => navigate(`/categories/${c.id}`)}
            />
          )}
        </Async>
    </>
  )
}
